import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import WhatsAppAdminButton from '../components/WhatsAppAdminButton';

const LISTER_CAP = 20;

const inputStyle = { width: '100%', background: 'var(--dark-3)', border: '1px solid rgba(201,168,76,0.2)', color: 'var(--text)', padding: '12px 16px', borderRadius: '4px', fontSize: '14px', outline: 'none' };
const labelStyle = { color: 'var(--text-muted)', fontSize: '12px', letterSpacing: '0.1em', textTransform: 'uppercase' as const, display: 'block', marginBottom: '8px' };

const RULES = [
  'All listings must be genuine — real properties/vehicles you have the right to list.',
  'No duplicate or spam listings across categories.',
  'Respond to buyer/renter enquiries promptly and honestly.',
  'For rentals: Photo 1 must clearly show the front of the building, Photo 2 must clearly show the unit\u2019s door with the door number visible.',
  'Unlock-fee revenue is tracked and paid out monthly after admin tallies it — no side collection from buyers.',
  'Submitting fraudulent photos or false information will result in permanent account termination and forfeiture of any accumulated earnings. This is non-negotiable.',
  'Admin may hide or remove any listing, or suspend your account, if these rules aren\u2019t followed.',
];

const ListerSignupPage = () => {
  const [email, setEmail] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [role, setRole] = useState('Caretaker');
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [listerCount, setListerCount] = useState<number | null>(null);

  useEffect(() => {
    supabase.rpc('lister_count').then(({ data, error: countError }) => {
      if (!countError && typeof data === 'number') setListerCount(data);
    });
  }, []);

  const capReached = listerCount !== null && listerCount >= LISTER_CAP;

  const handleSignup = async () => {
    setError('');
    if (capReached) {
      setError(`We've reached our cap of ${LISTER_CAP} listers for now. Please check back later.`);
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!whatsapp.trim()) {
      setError('Please enter a WhatsApp contact number.');
      return;
    }
    if (!agreed) {
      setError('Please confirm you agree to the rules below.');
      return;
    }

    setLoading(true);

    // Normalize to 254XXXXXXXXX so the same number can't slip past the
    // uniqueness check just by being typed with/without 0, +, or spaces.
    let normalizedWhatsapp = whatsapp.replace(/\D/g, '');
    if (normalizedWhatsapp.startsWith('0')) normalizedWhatsapp = '254' + normalizedWhatsapp.slice(1);
    else if (!normalizedWhatsapp.startsWith('254')) normalizedWhatsapp = '254' + normalizedWhatsapp;

    // Stash their details so the dashboard can create the profile row once
    // they click the magic link and actually get a session.
    localStorage.setItem('pending-lister-signup', JSON.stringify({ email, whatsapp: normalizedWhatsapp, role }));

    const { error: otpError } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
        emailRedirectTo: `${window.location.origin}/lister-dashboard`,
      },
    });

    setLoading(false);
    if (otpError) {
      setError(otpError.message);
      return;
    }
    setSuccess(true);
  };

  if (success) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--dark)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: 'var(--dark-3)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '8px', padding: '48px', width: '100%', maxWidth: '420px', textAlign: 'center' }}>
          <h1 className="serif" style={{ color: 'var(--gold)', fontSize: '26px', fontWeight: 300, marginBottom: '16px' }}>Check Your Email</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '24px', lineHeight: 1.6 }}>
            We've sent a magic link to <strong style={{ color: 'var(--text)' }}>{email}</strong>. Click it to finish setting up your account — no password needed.
          </p>
          <p style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
            Make sure to open the link on this same device/browser for the smoothest setup.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 20px' }}>
      <div style={{ background: 'var(--dark-3)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '8px', padding: '32px', width: '100%', maxWidth: '760px' }}>
        <h1 className="serif" style={{ color: 'var(--gold)', fontSize: '26px', fontWeight: 300, marginBottom: '4px' }}>Become a Lister</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '6px' }}>One Stop Shop — list Land, Houses, or Cars</p>

        {listerCount !== null && (
          <p style={{ color: capReached ? '#f87171' : 'var(--gold)', fontSize: '12px', marginBottom: '16px', fontWeight: 600 }}>
            {listerCount} / {LISTER_CAP} listers have joined so far{capReached ? ' — spots full' : ''}
          </p>
        )}

        {capReached ? (
          <div>
            <p style={{ color: '#f87171', fontSize: '14px', marginBottom: '20px', lineHeight: 1.6 }}>
              We've reached our cap of {LISTER_CAP} listers for now. Please check back later, or reach out to the admin below.
            </p>
            <div style={{ maxWidth: '320px' }}>
              <WhatsAppAdminButton />
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: '12px', textAlign: 'center', marginTop: '16px' }}>
              Already have an account? <Link to="/lister-login" style={{ color: 'var(--gold)' }}>Log in</Link>
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '28px' }} className="signup-grid">
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: '12px', marginBottom: '12px' }} className="field-grid">
                <div>
                  <label style={labelStyle}>Email</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>WhatsApp Contact</label>
                  <input value={whatsapp} onChange={e => setWhatsapp(e.target.value)} placeholder="e.g. 0712345678" style={inputStyle} />
                </div>
              </div>

              <p style={{ color: 'var(--text-muted)', fontSize: '11px', marginBottom: '16px' }}>
                No password needed — we'll email you a magic link to log in with, every time.
              </p>

              <div style={{ marginBottom: '12px', maxWidth: '260px' }}>
                <label style={labelStyle}>Role</label>
                <select value={role} onChange={e => setRole(e.target.value)} style={inputStyle}>
                  <option>Caretaker</option>
                  <option>Agent</option>
                  <option>Owner</option>
                </select>
              </div>

              <p style={{ color: 'var(--text-muted)', fontSize: '11px', lineHeight: 1.5, marginBottom: '12px', background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.12)', borderRadius: '6px', padding: '8px 12px' }}>
                💡 Share your listing links on WhatsApp status and groups — more people seeing it means more chances someone unlocks and pays, which adds up in your monthly earnings.
              </p>

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '14px', cursor: 'pointer' }}>
                <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{ marginTop: '3px' }} />
                <span style={{ color: 'var(--text-muted)', fontSize: '12px', lineHeight: 1.4 }}>I have read and agree to abide by the lister rules.</span>
              </label>

              {error && <p style={{ color: '#f87171', fontSize: '13px', marginBottom: '10px' }}>{error}</p>}

              <button onClick={handleSignup} className="brand-cta" style={{ width: '100%', marginBottom: '10px', background: 'linear-gradient(135deg, var(--gold), var(--gold-light))', color: '#000', border: 'none', padding: '12px', borderRadius: '4px', fontSize: '18px', cursor: 'pointer' }} disabled={loading}>
                {loading ? 'Sending...' : 'Send Magic Link'}
              </button>

              <p style={{ color: 'var(--text-muted)', fontSize: '12px', textAlign: 'center', marginBottom: '14px' }}>
                Already have an account? <Link to="/lister-login" style={{ color: 'var(--gold)' }}>Log in</Link>
              </p>

              <div style={{ borderTop: '1px solid rgba(201,168,76,0.15)', paddingTop: '12px' }}>
                <WhatsAppAdminButton />
              </div>
            </div>

            <div style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: '6px', padding: '16px', alignSelf: 'start' }}>
              <p style={{ color: 'var(--gold)', fontSize: '12px', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px', fontWeight: 600 }}>Lister Rules</p>
              <ul style={{ margin: 0, paddingLeft: '16px', color: 'var(--text-muted)', fontSize: '12px', lineHeight: 1.6 }}>
                {RULES.map((rule, i) => <li key={i} style={{ marginBottom: '6px' }}>{rule}</li>)}
              </ul>
            </div>
          </div>
        )}

        <style>{`
          @media (max-width: 700px) {
            .signup-grid { grid-template-columns: 1fr !important; }
            .field-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </div>
    </div>
  );
};

export default ListerSignupPage;
